import api from "./api";
import { toManualPaymentFormData } from "./paymentService";

// Unified shroff-counter payment desk (ShroffPaymentController). One search +
// one confirm route covering every payable type, instead of the per-type
// */manual-search + */manual-confirm pairs each module page drives on its own
// (paymentService.js, renewalService.js, clubRenewalService.js,
// idCardReprintService.js, bookingService.js).
//
// Search tries each type in a fixed order — booking → member/club
// registration → membership renewal → club renewal → ID card reprint — and
// returns the first match, tagged with a `payment_type` discriminator:
// "booking" | "member_registration" | "club_registration" |
// "membership_renewal" | "club_renewal" | "id_card_reprint". Confirm re-runs
// that same search and hands the request to the matching type's existing
// manualConfirm, so validation, amount matching and completion behavior are
// identical to the per-type routes.
//
// Gated by "search_shroff_payments" | "view_payments" (search) and
// "confirm_shroff_payments" | "submit_manual_payment_receipts" (confirm). The
// seeded Shroff role only carries the two *_shroff_payments permissions.

// GET /api/shroff/payments/search?payment_reference=… — returns the `payment`
// row, whose shape depends on payment_type (each one carries
// payment_reference, payable_amount, paid_amount, payment_status,
// payment_completed, paid_at; the payer/club/booking details differ per
// type — see ShroffPayments.jsx's describePayment()). 404 when no type
// matches.
export const searchShroffPayment = (paymentReference) =>
  api
    .get("/api/shroff/payments/search", { params: { payment_reference: paymentReference } })
    .then((r) => ({ ...r.data.payment, payment_type: r.data.payment_type || r.data.payment?.payment_type }));

// POST /api/shroff/payments/confirm — multipart. payload: { payment_reference,
// paid_amount, receipt_reference, paid_date?, note?, receipt? }.
// `receipt` (scanned file) is optional for a caller holding
// "confirm_shroff_payments" (ManualPaymentReceiptFile::rulesFor) and required
// otherwise — the shroff desk page never sends one. paid_amount must equal the
// payable amount exactly or the per-type manualConfirm 422s.
export const confirmShroffPayment = (payload) =>
  api.post("/api/shroff/payments/confirm", toManualPaymentFormData(payload)).then((r) => r.data);

// GET /api/shroff/payments/history — every desk/shroff confirmation recorded
// in manual_payment_receipts (ShroffPaymentController::history), newest
// paid_date first. Gated by its own "view_shroff_payment_history" — NOT
// view_payments; the backend seeder creates it but assigns it to no role.
// params (all optional): { date } (single paid_date, YYYY-MM-DD — from/to
// are ignored when set) | { from, to }, search (receipt_reference /
// payable_id / confirmed-by name), payable_type (raw backend value, e.g.
// "booking_payment", "member_registration_process"), status ("Pending" |
// "Approved" | "Rejected"), page, per_page (default 50, max 200).
// Returns { success, filters, rows, pagination: { page, per_page, total,
// last_page } }; each row is ManualPaymentApprovalController::formatReceipt's
// shape (same as listManualPaymentApprovals() rows — approval_status already
// comes back as "Acknowledged" rather than "Approved").
export const listShroffPaymentHistory = (params = {}) =>
  api.get("/api/shroff/payments/history", { params }).then((r) => ({
    rows: Array.isArray(r.data?.rows) ? r.data.rows : [],
    pagination: r.data?.pagination || { page: 1, per_page: params.per_page || 50, total: 0, last_page: 1 },
  }));
